import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SubmitButton from '../components/SubmitButton'
import LoadingSpinner from '../components/LoadingSpinner'
import { universityApi, UniversityMembership } from '../api/university'
import { toastUtil } from '../utils/toast'
import { REFERRAL_READY } from '../constants/featureFlags'
import {
  UNIVERSITY_BADGE,
  inviteUniversityFriends,
  shortUniversityName,
} from '../constants/university'

/**
 * 학교 인증 완료 (Figma `U5 가입 완료` — 6360:2236 / 6399:8404 / 6399:8424).
 *
 * 어느 학교인지는 **서버에 다시 물어서** 정한다. 주소에 학교를 실어 오면
 * 누구나 `/university/done?...` 을 쳐서 완료 화면을 볼 수 있다. 인증이 안 된
 * 사람은 학교 고르기로 돌려보낸다.
 *
 * 로고는 목록용(40px, 120px 파일)과 따로 큰 배지 파일을 쓴다. 여기서는 168px 로
 * 크게 보여 목록용을 늘리면 흐리다. 배지가 없는 학교는 목록 로고로 물러난다.
 */
const UniversityDone: React.FC = () => {
  const navigate = useNavigate()
  const [membership, setMembership] = useState<UniversityMembership | null>(null)

  useEffect(() => {
    let alive = true
    universityApi
      .getMembership()
      .then((m) => {
        if (!alive) return
        if (!m) {
          navigate('/university', { replace: true })
          return
        }
        setMembership(m)
      })
      .catch(() => {
        if (alive) navigate('/university', { replace: true })
      })
    return () => {
      alive = false
    }
  }, [navigate])

  /*
   * 그룹으로 가는 길은 친구 초대를 지나는 길 하나다(2026-11-04 도연님). 건너뛰기는 없다.
   * 이 화면은 기록에서 지운다(replace) — 그룹에서 뒤로 와도 다시 안 나온다.
   *
   * 친구 초대 화면이 꺼져 있으면(REFERRAL_READY) 공유창을 띄우고, 닫히면 그룹으로 간다.
   * 공유를 했는지는 묻지 않는다 — 앱 심사 규정상 공유를 조건으로 걸 수 없다.
   */
  const invite = async () => {
    if (REFERRAL_READY) {
      navigate('/invite?from=university', { replace: true })
      return
    }
    if (membership) await inviteUniversityFriends(membership.university.name, toastUtil)
    navigate('/university/group', { replace: true })
  }

  if (!membership) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-backgroundColorBg2">
        <LoadingSpinner />
      </div>
    )
  }

  const u = membership.university
  const badge = UNIVERSITY_BADGE[u.key] ?? u.logoPath

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col pb-32px">
        <div className="flex-1 flex flex-col items-center justify-center px-20px">
          {badge && (
            <img
              src={badge}
              alt={`${u.name} 로고`}
              className="w-168px h-168px object-contain"
            />
          )}
          <p className="mt-46px font-pretendard font-black text-24px leading-135% tracking-[-0.24px] text-brown-80%-main text-center">
            {shortUniversityName(u.name)} 인증이 완료되었어요
          </p>
          <p className="mt-10px font-pretendard font-regular text-15px leading-150% tracking-[-0.15px] text-textColorText2 text-center">
            이제 우리 학교 사람들의 기록을 볼 수 있어요
          </p>
        </div>

        <div className="w-full px-20px">
          <SubmitButton text="친구 초대하기" onSubmit={invite} />
        </div>
      </div>
    </div>
  )
}

export default UniversityDone
